import { Box, Button, Flex, Text, Tooltip } from '@modusign/design-system';
import moment from 'moment';

import InfoCallout from '@app/components/InfoCallout';
import { useExternalDocumentPermission } from '@app/features/externalDocument/hooks/useExternalDocumentPermission';

import { ExternalDocument } from '../../types/ExternalDocument';
import formatInternationalPhoneNumber from '../../utils/formatInternationalPhoneNumber';
import statusToString from '../../utils/statusToString';
import typeToString from '../../utils/typeToString';
import DetailInfoItem from '../DetailInfoItem';

export default function BasicInformationViewer({
  externalDocument,
  onClickEdit,
}: {
  externalDocument: ExternalDocument;
  onClickEdit: () => void;
}) {
  const { updatePermission } = useExternalDocumentPermission(externalDocument.id);

  return (
    <Box>
      {!externalDocument.personInCharge && (
        <InfoCallout sx={{ mt: 7 }}>계약 정보를 입력하고 더 효율적으로 관리해 보세요.</InfoCallout>
      )}
      <Flex
        borderTop="1px solid"
        borderTopColor="lightGray300"
        flexDirection="column"
        minHeight="60px"
        mt={7}
      >
        <DetailInfoItem name="계약 제목">
          <Text
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {externalDocument.title}
          </Text>
        </DetailInfoItem>
        <DetailInfoItem name="계약 체결일" wrapperFlex="1 1 0">
          {externalDocument.dateOfCompletion
            ? moment(externalDocument.dateOfCompletion).format('YYYY.MM.DD')
            : '-'}
        </DetailInfoItem>
        <DetailInfoItem name="계약서 형태" wrapperFlex="1 1 0">
          {typeToString(externalDocument.type)}
        </DetailInfoItem>
        <DetailInfoItem name="계약 기간" wrapperFlex="1 1 0">
          {externalDocument.documentPeriod
            ? `${moment(externalDocument.documentPeriod.from).format('YYYY.MM.DD')} ~ ${
                externalDocument.documentPeriod.to
                  ? moment(externalDocument.documentPeriod.to).format('YYYY.MM.DD')
                  : ''
              }`
            : '-'}
        </DetailInfoItem>
        <DetailInfoItem name="계약 상태" wrapperFlex="1 1 0">
          {statusToString(externalDocument.status)}
        </DetailInfoItem>
        <DetailInfoItem name="계약 갱신 여부">
          {externalDocument.renewal ? (
            <Flex flexDirection="column">
              <Text>갱신함</Text>
              <Text as="p" color="textNeutralLight" fontSize="sm" mt={1}>
                {externalDocument.renewal.noticeBeforeExpiration && (
                  <>
                    계약 종료일 기준 {externalDocument.renewal.noticeBeforeExpiration.value}
                    일 전까지 통지가 필요합니다.
                    <br />
                  </>
                )}
                {externalDocument.renewal.period.value}년마다 계약을 갱신합니다. 다음 계약일은{' '}
                {moment(externalDocument.dateOfCompletion)
                  .add(externalDocument.renewal.period.value, 'year')
                  .format('YYYY.MM.DD')}
                입니다.
              </Text>
            </Flex>
          ) : (
            '갱신 안 함'
          )}
        </DetailInfoItem>
        <DetailInfoItem name="담당자" showAlert={!externalDocument.personInCharge}>
          {externalDocument.personInCharge ? (
            <>
              <Text>{externalDocument.personInCharge.name}</Text>
              <Text color="textNeutralLight" fontSize="sm" ml={1}>
                {[
                  externalDocument.personInCharge.email,
                  externalDocument.personInCharge.phone
                    ? formatInternationalPhoneNumber(externalDocument.personInCharge.phone)
                    : '',
                ]
                  .filter(Boolean)
                  .join(' / ')}
              </Text>
            </>
          ) : (
            '-'
          )}
        </DetailInfoItem>
        <DetailInfoItem name="원본 보관 위치">
          <Text
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {externalDocument.originalLocation || '-'}
          </Text>
        </DetailInfoItem>
        <DetailInfoItem name="메모">
          <Text as="p">{externalDocument.memo || '-'}</Text>
        </DetailInfoItem>
      </Flex>
      <Flex
        alignItems="center"
        flexDirection="row-reverse"
        justifyContent="space-between"
        mb={[10, null, 0]}
        mt={9}
      >
        <Tooltip
          content={updatePermission.message}
          disabled={updatePermission.isPermitted}
          placement="top"
        >
          <Button disabled={!updatePermission.isPermitted} type="button" onClick={onClickEdit}>
            수정하기
          </Button>
        </Tooltip>
      </Flex>
    </Box>
  );
}
